import { useState, useEffect } from "react";
import {
	changeApiSettings,
	getConfiguration,
} from "../components/services/chat.service";
import { useChatState } from "../components/state/ChatState";
import { useSearchParams } from "react-router-dom";
import { IConfiguration } from "../components/interfaces/types";

const baseContainerUrl =
	"https://symantodigitalfriend.blob.core.windows.net/configurations/";

const formatConfigResponse = (
	configData: any,
	environment = "prod",
	variation = "default",
) => {
	let result: { [key: string]: any } = {};
	configData[environment]
		.filter((item: any) => item.name.includes(variation.toUpperCase()))
		.forEach(({ name, value }: any) => {
			result[name.replace(`EXPOSE_${variation.toUpperCase()}_`, "")] = value;
		});

	if (
		Object.keys(result).length === 0 ||
		result?.API_KEY === undefined ||
		result?.BASE_API_URL === undefined
	) {
		throw new Error("Configuration is missing required fields");
	}

	return result;
};

export const useConfiguration = () => {
	const chatState = useChatState(); // global state
	const [configurationLoading, setConfigurationLoading] = useState(true);
	const [searchParams] = useSearchParams();

	useEffect(() => {
		const host = window.location.host.includes("localhost")
			? ["localhost:3000"]
			: window.location.host.split(".");
		const variation = searchParams.get("variation") || "default";
		const env = searchParams.get("env") ?? "prod";

		if (window.localStorage.getItem("debugConfig") == "true") {
			console.log("env", env);
		}

		let config: string | undefined = undefined;

		if (window.localStorage.getItem("debugConfig") == "true") {
			console.log("window.location.pathname", window.location.pathname);
		}
		if (window.location.pathname != "/") {
			config = window.location.pathname.replaceAll("/", "");
		} else if (host.length > 2) {
			config = host
				.filter((i) => i != "www")
				.slice(0, -2)
				.join("");
		} else {
			config = searchParams.get("config") ?? undefined;
		}

		try {
			if (window.localStorage.getItem("debugConfig") == "true") {
				console.info("Configuration debug info:", {
					config,
					env,
					variation,
					host,
					pathname: window.location.pathname,
					searchParams: Object.fromEntries(Array.from(searchParams)),
				});
			}
		} catch (e) {
			console.error;
		}

		if (config) {
			// check if config is a valid agent uuid
			const isValidUuid =
				/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
					config,
				);

			let agentId: string | null = null;

			if (isValidUuid) {
				agentId = config;

				config = host
					.filter((i) => i != "www")
					.slice(0, -2)
					.join("");
			}

			if (window.location.host.includes("localhost") && config == "") {
				config = "international-chat";
			}

			fetch(`${baseContainerUrl}${config}.json`)
				.then((res) => res.json())
				.then((result) => {
					const _cfg = formatConfigResponse(result, env, variation);

					if (agentId != null) {
						// agentIdOverride if path param is valid agent id
						let agentIdsDefs = _cfg["AGENT_IDS"]?.split("|")?.length ?? 1;
						_cfg["AGENT_IDS"] = new Array(agentIdsDefs).fill(agentId).join("|");
					}

					chatState.setBaseConfig(_cfg as IConfiguration);
				})
				.catch(console.error)
				.finally(() => setConfigurationLoading(false));
		} else {
			getConfiguration()
				.then((result) => {
					changeApiSettings(result.BASE_API_URL, result.API_KEY);
					chatState.setBaseConfig(result);
				})
				.catch(console.error)
				.finally(() => setConfigurationLoading(false));
		}
	}, []);

	return {
		configurationLoading,
		configParam: searchParams.get("config"),
		envParam: searchParams.get("env"),
	};
};
